import { SlCheckbox, SlIcon } from '@shoelace-style/shoelace/dist/react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Pagination } from '../Pagination';
import { getNestedValue } from '../utils';
import { DnDSubRowProps } from './DnDSubRow';
import DnDTableBodyRow from './DnDTableBodyRow';

export interface ColumnData<T extends Record<string, any> = {}> {
  header: string;
  field: string;
  id: string;
  sort?: boolean;
  width?: number;
  renderer?: (data: T) => React.ReactNode;
  headerRenderer?: (data: Pick<ColumnData<T>, 'field' | 'sort' | 'header' | 'id'>) => React.ReactNode;
  show?: boolean;
}

export interface DnDTableProps<T> {
  data: Array<T & { subRows?: Array<T> }>;
  columns: Array<ColumnData<T>>;
  rowSelection?: boolean;
  onSelectionChange?: (data: Array<T>) => void;
  noDataText: string;
  rowsPerPage?: number;
  variant?: 'filled' | 'outlined';
  expandable?: boolean;
  subrowProps?: DnDSubRowProps;
  draggable?: boolean;
  onReorder?: (reorderedData: Array<T>) => void;
}

const uuid = () => Math.random().toString(16).slice(-8);

const DnDTableComponent = <T extends Record<string, any>>(props: DnDTableProps<T>) => {
  const locale = localStorage.getItem('co4:app/language') ?? 'de';
  const [t] = useTranslation();
  const {
    data: ogData,
    columns,
    rowSelection = false,
    onSelectionChange = () => {},
    noDataText = t('noRecordsFound'),
    rowsPerPage = 10,
    variant = 'outlined',
    expandable = false,
    draggable = true,
    onReorder = () => {},
  } = props;
  const [selection, setSelection] = React.useState<Array<string>>([]);
  const [sort, setSort] = React.useState({ field: '', order: 'asc' });
  const [pagination, setPagination] = React.useState({
    currentPage: 1,
    rowsPerPage,
    totalPages: 1,
  });
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const data: Array<T & { selectionId?: string }> = React.useMemo(() => {
    setPagination(({ rowsPerPage }) => ({
      currentPage: 1,
      rowsPerPage,
      totalPages: Math.ceil(ogData.length / rowsPerPage),
    }));

    return ogData.map((d) => ({ ...d, selectionId: uuid() }));
  }, [ogData]);

  const currentRange = React.useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.rowsPerPage;
    const end = start + pagination.rowsPerPage > data.length ? data.length : start + pagination.rowsPerPage;
    return { start, end };
  }, [pagination, data.length]);

  const sortBy = React.useCallback(
    (a, b) => {
      const { field, order } = sort;
      if (!field) return 0;
      const valA = getNestedValue(a, field);
      const valB = getNestedValue(b, field);
      const multiplier = order === 'asc' ? 1 : -1;

      if (typeof valA === 'number' && typeof valB === 'number') {
        return order === 'asc' ? valA - valB : valB - valA;
      } else if (typeof valA === 'string' && typeof valB === 'string') {
        return multiplier * valA?.localeCompare(valB, locale);
      } else return 0;
    },
    [sort, locale],
  );

  const [currentPageData, setCurrentPageData] = React.useState<Array<T & { selectionId?: string }>>([]);

  React.useEffect(() => {
    setCurrentPageData(data.sort(sortBy).slice(currentRange.start, currentRange.end));
  }, [data, sortBy, currentRange]);

  const handleHeaderClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const { id: field } = e.target as HTMLHeadElement;
      const order = sort.order === 'asc' ? 'desc' : 'asc';

      if (field === sort.field) {
        setSort((prev) => ({ ...prev, order }));
      } else {
        setSort({ field, order: 'asc' });
      }
    },
    [sort],
  );

  const handleSelectionChange = (id: string | null, all?: boolean) => {
    let updatedSelections = [...selection];

    if (all !== undefined) {
      updatedSelections = all ? currentPageData.map((d) => d.selectionId!) : [];
    } else if (id !== null) {
      if (updatedSelections.includes(id)) {
        updatedSelections.splice(updatedSelections.indexOf(id), 1);
      } else {
        updatedSelections.push(id);
      }
    }

    const selectionData = all
      ? [...currentPageData]
      : updatedSelections.map((sel) => currentPageData.find((d) => d.selectionId === sel)!).filter(Boolean);

    onSelectionChange(selectionData as T[]);
    setSelection(updatedSelections);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCurrentPageData((items) => {
        const oldIndex = items.findIndex((item) => item.selectionId === active.id);
        const newIndex = items.findIndex((item) => item.selectionId === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update the entire data array and notify parent
        const newData = [...data];
        const startIndex = currentRange.start;

        for (let i = 0; i < newItems.length; i++) {
          newData[startIndex + i] = newItems[i];
        }

        onReorder(newData as T[]);

        return newItems;
      });
    }

    setActiveId(null);
  };

  let areAllSelected =
    rowSelection && selection.length !== 0 && currentPageData.every((d) => selection.includes(d.selectionId!));

  const onPageChange = (currentPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage }));
  };

  const onRowsChange = (rowsPerPage: number) => {
    setPagination({
      currentPage: 1,
      rowsPerPage,
      totalPages: Math.ceil(data.length / rowsPerPage),
    });
  };

  const renderDragOverlay = useCallback(() => {
    if (!activeId) return null;
    const activeItem = currentPageData.find((item) => item.selectionId === activeId);
    if (!activeItem) return null;

    return (
      <table>
        <tbody>
          <tr>
            {draggable && (
              <td className="drag-handle-td">
                <SlIcon name="grip-vertical" />
              </td>
            )}
            {rowSelection && (
              <td>
                <div className="selection-box head-col">
                  <SlCheckbox />
                </div>
              </td>
            )}
            {expandable && <td className="expand-button-td"></td>}
            {columns.map((col) => {
              if (col.show === false) return null;
              return (
                <td key={col.id}>
                  {col.renderer
                    ? col.renderer({ ...activeItem, rowValue: getNestedValue(activeItem, col.field) })
                    : getNestedValue(activeItem, col.field) ?? '-'}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    );
  }, [activeId, currentPageData, columns, draggable, rowSelection, expandable]);

  return (
    <>
      <div className={`dnd-table-wrapper ${variant}`}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          <table>
            <thead>
              <tr>
                {draggable && <th className="drag-handle-td"></th>}
                {rowSelection && (
                  <th id="select-all-box">
                    <div id="select-all" className="head-col selection-box ">
                      <SlCheckbox
                        disabled={!data.length}
                        checked={areAllSelected}
                        onSlChange={() => {
                          handleSelectionChange(null, !areAllSelected);
                        }}></SlCheckbox>
                    </div>
                  </th>
                )}
                {expandable && <th className="expand-button-td"></th>}

                {columns.map((col) => {
                  const { id, field, header, headerRenderer, width = 'auto', sort: colSort = true, show } = col;
                  const icon =
                    sort.field === field && sort.order === 'desc' ? 'sort-alpha-down-alt' : 'sort-alpha-down';

                  if (show === false) return null;

                  return (
                    <th key={id} id={field} style={{ width }} onClick={colSort ? handleHeaderClick : () => {}}>
                      {headerRenderer ? (
                        headerRenderer({ field, header, id, sort: colSort })
                      ) : (
                        <div id={field} className="head-col" data-value={header}>
                          {header}
                          {colSort && <SlIcon name={icon}></SlIcon>}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                <SortableContext
                  items={currentPageData.map((item) => item.selectionId!)}
                  strategy={verticalListSortingStrategy}>
                  {currentPageData.map((item, index) => (
                    <DnDTableBodyRow
                      key={`row-${item.selectionId}`}
                      id={item.selectionId!}
                      expandable={expandable}
                      rowSelection={rowSelection}
                      selected={selection.includes(item.selectionId!)}
                      onCheckboxToggle={handleSelectionChange}
                      columns={columns}
                      subrowProps={props?.subrowProps ?? ({} as DnDSubRowProps)}
                      index={index}
                      data={item}
                      draggable={draggable}
                    />
                  ))}
                </SortableContext>
              ) : (
                <tr>
                  <td colSpan={columns.length + +rowSelection + +expandable + +draggable}>{noDataText}</td>
                </tr>
              )}
            </tbody>
          </table>
          <DragOverlay>{renderDragOverlay()}</DragOverlay>
        </DndContext>
      </div>
      {(pagination.totalPages > 1 || pagination.rowsPerPage !== rowsPerPage) && (
        <Pagination
          currentRange={currentRange}
          totalData={data.length}
          totalPages={pagination.totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={onPageChange}
          onRowsChange={onRowsChange}
          activePage={pagination.currentPage}
        />
      )}
    </>
  );
};

export const DnDTable = React.memo(DnDTableComponent) as typeof DnDTableComponent;
